import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Center, Heading, HStack, Text, VStack,
} from '@gluestack-ui/themed';
import type { ReceivedDataMessage } from '@livekit/components-core';
import {
  useDataChannel, useLocalParticipant, useTracks,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import {
  BriefingConferenceStage,
  isLecturerParticipant,
  ORAL_EXAM_BRIEFING_TOPIC,
  parseBriefingDeparturePayload,
  type SpeakPermissionPayload,
} from '@/common/components/oralExam/BriefingConferenceStage';
import {
  VerticalFlowStepper,
  type FlowStepDescriptor,
} from '@/common/components/oralExam/VerticalFlowStepper';

const BRIEFING_TRANSFER_TIMEOUT_MS = 15_000;

export type StudentGroupBriefingScreenProps = {
  slotId?: string;
  brieferId?: string | null;
  slotOwnerId?: string | null;
  /** Parent refetches run state when a supervisor publishes `started`. */
  onServerCheckRequested?: () => void;
};

/**
 * Subscribe-only cohort briefing view: lecturer-large conference layout, no exam controls.
 */
export function StudentGroupBriefingScreen({
  slotId,
  brieferId,
  slotOwnerId,
  onServerCheckRequested,
}: StudentGroupBriefingScreenProps) {
  const { t } = useTranslation();
  const { localParticipant } = useLocalParticipant();
  const [transferActive, setTransferActive] = useState(false);
  const lastStartingAtRef = useRef<number | null>(null);
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: true },
  );
  const lecturerTrack = cameraTracks.find(
    (ref) => isLecturerParticipant(ref.participant),
  );

  useEffect(() => {
    localParticipant.setMicrophoneEnabled(false);
  }, [localParticipant]);

  const setTransferVisible = useCallback((visible: boolean) => {
    setTransferActive(visible);
    if (!visible) {
      lastStartingAtRef.current = null;
    }
  }, []);

  const onBriefingData = useCallback((msg: ReceivedDataMessage) => {
    if (!msg.from || !isLecturerParticipant(msg.from)) return;

    const departure = parseBriefingDeparturePayload(msg.payload);
    if (departure) {
      if (departure.type === 'starting') {
        lastStartingAtRef.current = Date.now();
        setTransferVisible(true);
        return;
      }
      if (departure.type === 'started') {
        onServerCheckRequested?.();
        return;
      }
      if (departure.type === 'cancelled') {
        setTransferVisible(false);
      }
      return;
    }

    try {
      const parsed = JSON.parse(new TextDecoder().decode(msg.payload)) as Partial<SpeakPermissionPayload>;
      if (parsed?.type !== 'oralExam.speakPermission') return;
      if (parsed.targetIdentity !== localParticipant.identity) return;
      localParticipant.setMicrophoneEnabled(Boolean(parsed.allowed));
    } catch {
      /* ignore malformed packets */
    }
  }, [localParticipant, onServerCheckRequested, setTransferVisible]);

  useDataChannel(ORAL_EXAM_BRIEFING_TOPIC, onBriefingData);

  useEffect(() => {
    if (!transferActive) return undefined;
    const timer = setInterval(() => {
      const lastStartingAt = lastStartingAtRef.current;
      if (lastStartingAt != null && Date.now() - lastStartingAt >= BRIEFING_TRANSFER_TIMEOUT_MS) {
        setTransferVisible(false);
      }
    }, 250);
    return () => clearInterval(timer);
  }, [setTransferVisible, transferActive]);

  const steps: FlowStepDescriptor[] = useMemo(() => [
    {
      key: 'consent',
      label: t('oralExam.groupBriefing.steps.consent', { defaultValue: 'Consent' }),
      status: 'done',
    },
    {
      key: 'screenShare',
      label: t('oralExam.groupBriefing.steps.screenShare', { defaultValue: 'Share your screen' }),
      status: 'done',
    },
    {
      key: 'device',
      label: t('oralExam.groupBriefing.steps.device', { defaultValue: 'Camera & mic' }),
      status: 'done',
    },
    {
      key: 'identity',
      label: t('oralExam.groupBriefing.steps.identity', { defaultValue: 'Identity check' }),
      status: 'done',
    },
    {
      key: 'groupBriefing',
      label: t('oralExam.groupBriefing.steps.groupBriefing', { defaultValue: 'Group briefing' }),
      subtitle: t('oralExam.groupBriefing.steps.groupBriefingSubtitle', { defaultValue: 'listen now' }),
      status: 'active',
    },
    {
      key: 'exam',
      label: t('oralExam.groupBriefing.steps.exam', { defaultValue: 'Your exam' }),
      subtitle: t('oralExam.groupBriefing.steps.examSubtitle', { defaultValue: 'starts automatically' }),
      status: 'upcoming',
    },
  ], [t]);

  const prepRows = [
    {
      key: 'listen',
      label: t('oralExam.groupBriefing.prepListen', {
        defaultValue: 'Listen for final instructions',
      }),
      done: Boolean(lecturerTrack),
    },
    {
      key: 'mediaReady',
      label: t('oralExam.groupBriefing.prepMediaReady', {
        defaultValue: 'Keep camera, microphone, and screen ready',
      }),
      done: true,
    },
    {
      key: 'autoStart',
      label: t('oralExam.groupBriefing.prepAutoStart', {
        defaultValue: 'Your personal exam starts automatically',
      }),
      done: false,
    },
  ];

  return (
    <Box flex={1} bg="$bgApp" testID="student-briefing-screen">
      <HStack flex={1} space="lg" p="$6" alignItems="flex-start">
        <Box width={286} flexShrink={0}>
          <VerticalFlowStepper
            testID="student-briefing-stepper"
            steps={steps}
            density="comfortable"
          />
        </Box>

        <Box
          flex={1}
          maxWidth={854}
          alignSelf="stretch"
          width="100%"
          bg="$bgCard"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$xl"
          overflow="hidden"
          p="$6"
          testID="student-briefing-card"
        >
          <VStack space="xl" flex={1}>
            <VStack space="sm">
              <Text size="xs" color="$textSubtle" fontWeight="bold" textTransform="uppercase">
                {t('oralExam.groupBriefing.phaseLabel', { defaultValue: 'Group briefing' })}
              </Text>
              <Heading size="xl" color="$textHeading" testID="student-briefing-heading">
                {transferActive
                  ? t('oralExam.groupBriefing.examStartingTitle', {
                    defaultValue: 'Your exam is starting',
                  })
                  : t('oralExam.groupBriefing.title', {
                    defaultValue: 'Group briefing',
                  })}
              </Heading>
              {!transferActive ? (
                <Text size="md" color="$textBody" testID="student-briefing-body">
                  {t('oralExam.groupBriefing.body', {
                    defaultValue: 'Listen for final instructions. Your personal exam starts automatically after this briefing.',
                  })}
                </Text>
              ) : null}
            </VStack>

            {transferActive ? (
              <Center flex={1} minHeight={180} testID="student-briefing-transfer">
                <Text size="lg" color="$textHeading" fontWeight="semibold" textAlign="center">
                  {t('oralExam.groupBriefing.movingToExamRoom', {
                    defaultValue: 'Moving you to your exam room.',
                  })}
                </Text>
              </Center>
            ) : (
              <BriefingConferenceStage
                slotId={slotId}
                brieferId={brieferId}
                slotOwnerId={slotOwnerId}
              />
            )}

            <Box
              borderTopWidth={1}
              borderTopColor="$borderLight"
              pt="$4"
              testID="student-briefing-prep-area"
            >
              <VStack space="md">
                {prepRows.map((row) => (
                  <HStack
                    key={row.key}
                    alignItems="center"
                    space="sm"
                    testID={`student-briefing-prep-${row.key}`}
                  >
                    <Box
                      w={20}
                      h={20}
                      borderRadius="$full"
                      bg={row.done ? '$statusSuccess' : '$borderMedium'}
                      alignItems="center"
                      justifyContent="center"
                    >
                      {row.done && (
                        <Text size="2xs" color="$textInverted" fontWeight="bold">✓</Text>
                      )}
                    </Box>
                    <Text size="sm" color="$textBody" flex={1}>
                      {row.label}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        </Box>
      </HStack>
    </Box>
  );
}
